import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import VideoModal from '../components/VideoModal';
import ThreeCirclesModal, { EMPTY_THREE_CIRCLES, ThreeCirclesData } from '../components/ThreeCirclesModal';
import StepShareDialog from '../components/StepShareDialog';
import { HTMLService } from '../services/htmlService';
import { progressService } from '../services/progressService';
import { SponsorService, Sponsor } from '../services/sponsorService';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import { useProfileModal } from '../context/ProfileModalContext';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import styles from '../styles/stepCommon.module.css';
import circleStyles from '../styles/threeCirclesModal.module.css';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { getStepActionSchema } from '../config/stepActionSchemas';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';
import StepHelpButton from '../components/StepHelpButton';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';

export type SponsorStatus = 'have_sponsor' | 'looking' | 'ask_in_meeting';

interface GettingStartedProps {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const READINGS_FILE = '/content/010_GETTING_STARTED_READINGS.md';

const GettingStarted: React.FC<GettingStartedProps> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const { openProfileModal, isProfileModalOpen } = useProfileModal();
  const [isAddictionReaderOpen, setIsAddictionReaderOpen] = useState(false);
  const [isFellowshipReaderOpen, setIsFellowshipReaderOpen] = useState(false);
  const [is3CirclesReaderOpen, setIs3CirclesReaderOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isCirclesModalOpen, setIsCirclesModalOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [threeCircles, setThreeCircles] = useState<ThreeCirclesData>(EMPTY_THREE_CIRCLES);
  const [sponsorStatus, setSponsorStatus] = useState<SponsorStatus | ''>('');
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [actions, setActions] = useState<ActionItem[]>([]);
  const [isInitializing, setIsInitializing] = useState(true);
  const prevProfileModalOpen = useRef(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: 'Read about our addiction',
      description: 'Introduction and Our Addiction from the Green Book',
      completed: false,
      hasButton: true,
      buttonText: 'Read Green Book',
      buttonAction: () => setIsAddictionReaderOpen(true),
    },
    {
      id: 2,
      title: 'Read about our fellowship',
      description: 'Meetings and sponsorship from the Green Book',
      completed: false,
      hasButton: true,
      buttonText: 'Read Green Book',
      buttonAction: () => setIsFellowshipReaderOpen(true),
    },
    {
      id: 3,
      title: 'My sponsor',
      description: 'To effectively work the steps, I need to have a sponsor. Where are you in that process?',
      completed: false,
    },
    {
      id: 4,
      title: 'Read about the 3 Circles',
      description: 'How SAA members define abstinence using the Three Circles tool',
      completed: false,
      hasButton: true,
      buttonText: 'Read Green Book',
      buttonAction: () => setIs3CirclesReaderOpen(true),
    },
    {
      id: 5,
      title: 'Watch: The 3 Circles of SAA',
      description: 'Video diving deeper into this valuable tool',
      completed: false,
      hasButton: true,
      buttonText: 'Watch Video',
      buttonAction: () => setIsVideoModalOpen(true),
    },
    {
      id: 6,
      title: 'Define my 3 Circles',
      description:
        'Enter/update my personal 3 circles: inner (acting out), middle (warning signs), and outer (supports recovery). I realize that these can change as I go through recovery and that I will only make changes after talking with my sponsor and others in the program.',
      completed: false,
      hasButton: true,
      buttonText: 'Enter/update my 3 Circles',
      buttonAction: () => setIsCirclesModalOpen(true),
    },
    {
      id: 7,
      title: 'Share and review your work with your sponsor',
      description:
        'Review your Getting Started progress and 3 Circles with your sponsor. You can check this off when you\'ve shared or discussed your work — using the share button is optional.',
      completed: false,
      hasButton: true,
      buttonText: 'Share with sponsor',
      buttonAction: () => setIsShareDialogOpen(true),
    },
    {
      id: 8,
      title: 'Ready for Step One',
      description: 'You have completed the introductory work and are ready to begin Step One.',
      completed: false,
    },
  ];

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa?tab=steps', active: false },
    { label: 'Getting Started', path: '/saa/12-steps/getting-started', active: true },
  ];

  const mergeActions = (saved: { actions?: unknown; action_schema_version?: number }, stepCompleted: boolean) =>
    mergeStepActions(0, originalActions, saved, stepCompleted);

  const saveProgress = async (
    updatedActions: ActionItem[],
    extraData?: { three_circles?: ThreeCirclesData; sponsor_status?: SponsorStatus | '' }
  ) => {
    const allCompleted = updatedActions.every((a) => a.completed);
    const schemaVersion = getStepActionSchema(0).actionSchemaVersion;
    await progressService.updateStepProgress({
      program: 'SAA',
      track: '12-steps',
      step_number: 0,
      step_title: 'Getting Started',
      completed: allCompleted,
      data: stepDataWithActions(updatedActions, schemaVersion, {
        three_circles: extraData?.three_circles ?? threeCircles,
        sponsor_status: extraData?.sponsor_status ?? sponsorStatus,
      }),
    });
  };

  const loadSponsors = async () => {
    if (!user) {
      setSponsors([]);
      return;
    }
    try {
      const list = await SponsorService.getSponsors();
      setSponsors(list);
    } catch {
      setSponsors([]);
    }
  };

  const loadPageData = async () => {
    if (!user) {
      setActions(originalActions);
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);
    try {
      perfMark('GettingStarted', 'loadPageData start');
      const [existing, list] = await perfTimeAsync('GettingStarted loadPageData (total)', () =>
        Promise.all([
          progressService.loadStepPageProgress('SAA', '12-steps', 0),
          perfTimeAsync('GET /sponsors', () => SponsorService.getSponsors().catch(() => [] as Sponsor[])),
        ])
      );
      setSponsors(list);

      if (existing?.data) {
        const saved = existing.data;
        if (saved.actions) {
          const merged = mergeActions(saved, !!existing.completed);
          setActions(merged.actions);
          if (merged.migrationApplied) {
            await progressService.updateStepProgress({
              program: 'SAA',
              track: '12-steps',
              step_number: 0,
              step_title: 'Getting Started',
              completed: !!existing.completed,
              data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, saved),
            });
          }
        } else {
          setActions(originalActions);
        }
        if (saved.three_circles) {
          setThreeCircles({
            inner: saved.three_circles.inner || [],
            middle: saved.three_circles.middle || [],
            outer: saved.three_circles.outer || [],
          });
        }
        if (saved.sponsor_status) {
          setSponsorStatus(saved.sponsor_status);
        }
      } else {
        setActions(originalActions);
      }
      clearError();
      perfSummary('Getting Started page');
    } catch (err: any) {
      handleError(err, 'Failed to load your progress. Please try refreshing the page.');
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    loadPageData();
  }, [user, stepWorkspaceRevision]);

  useEffect(() => {
    if (prevProfileModalOpen.current && !isProfileModalOpen && user) {
      loadSponsors();
    }
    prevProfileModalOpen.current = isProfileModalOpen;
  }, [isProfileModalOpen, user]);

  const circlesComplete = (circles: ThreeCirclesData) =>
    circles.inner.length >= 1 && circles.middle.length >= 1 && circles.outer.length >= 1;

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    if (!user) {
      handleError(new Error('User not logged in'), 'Please sign in to save your progress.');
      return;
    }
    try {
      setActions(updatedActions);
      await saveProgress(updatedActions);
    } catch (err: any) {
      handleError(err, 'Failed to save progress. Please try again.');
    }
  };

  const handleSponsorStatusChange = async (status: SponsorStatus) => {
    setSponsorStatus(status);
    if (!user) return;
    try {
      await saveProgress(actions, { sponsor_status: status });
    } catch (err: any) {
      handleError(err, 'Failed to save sponsor status.');
    }
  };

  const handleThreeCirclesSave = useCallback(
    async (circles: ThreeCirclesData) => {
      setThreeCircles(circles);
      if (!user) return;

      let updatedActions = actions;
      const action6 = actions.find((a) => a.id === 6);
      if (action6?.completed && !circlesComplete(circles)) {
        updatedActions = actions.map((a) => (a.id === 6 ? { ...a, completed: false } : a));
        setActions(updatedActions);
      }

      await saveProgress(updatedActions, { three_circles: circles });
    },
    [user, actions, threeCircles, sponsorStatus]
  );

  const canShareGettingStarted = () => {
    const hasCompletedWork = actions.some((a) => a.completed && a.id !== 7 && a.id !== 8);
    const hasCircles =
      threeCircles.inner.length > 0 ||
      threeCircles.middle.length > 0 ||
      threeCircles.outer.length > 0;
    return hasCompletedWork || hasCircles;
  };

  const buildGettingStartedShareHtml = () =>
    HTMLService.generateGettingStartedSummaryHTML(
      { name: user?.name || user?.display_name, email: user?.email },
      actions.map(({ id, completed }) => ({ id, completed })),
      threeCircles,
      sponsorStatus || undefined
    );

  const canCompleteAction = (actionId: number) => {
    if (actionId === 6) return circlesComplete(threeCircles);
    return true;
  };

  const saaSponsors = sponsors.filter((s) => s.program === 'SAA');

  const renderCirclesPreview = () => {
    const hasAny =
      threeCircles.inner.length > 0 ||
      threeCircles.middle.length > 0 ||
      threeCircles.outer.length > 0;
    if (!hasAny) return null;

    const sections = [
      { key: 'inner' as const, label: 'Inner circle — behaviors I identify as acting out' },
      { key: 'middle' as const, label: 'Middle — warning signs' },
      { key: 'outer' as const, label: 'Outer — supports recovery' },
    ];

    return (
      <div className={styles.actionContentIndent}>
        <div className={circleStyles.circlesPreview}>
          {sections.map(({ key, label }) =>
            threeCircles[key].length > 0 ? (
              <div key={key} className={circleStyles.circlesPreviewSection}>
                <p className={circleStyles.circlesPreviewHeading}>{label}</p>
                <ul className={circleStyles.circlesPreviewList}>
                  {threeCircles[key].map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null
          )}
        </div>
      </div>
    );
  };

  const renderSponsorContent = () => (
    <div className={styles.actionContentIndent}>
      <div className={circleStyles.sponsorOptions}>
        <label className={circleStyles.sponsorOption}>
          <input
            type="radio"
            name="sponsor-status"
            checked={sponsorStatus === 'have_sponsor'}
            onChange={() => handleSponsorStatusChange('have_sponsor')}
          />
          I have a sponsor
        </label>
        <label className={circleStyles.sponsorOption}>
          <input
            type="radio"
            name="sponsor-status"
            checked={sponsorStatus === 'looking'}
            onChange={() => handleSponsorStatusChange('looking')}
          />
          I&apos;m looking for a sponsor
        </label>
        <label className={circleStyles.sponsorOption}>
          <input
            type="radio"
            name="sponsor-status"
            checked={sponsorStatus === 'ask_in_meeting'}
            onChange={() => handleSponsorStatusChange('ask_in_meeting')}
          />
          I&apos;ll ask someone in a meeting
        </label>
      </div>
      <button
        type="button"
        className={styles.actionButton}
        onClick={() => openProfileModal('sponsorship')}
      >
        Open Profile → Sponsorship
      </button>
      {saaSponsors.length > 0 && (
        <div className={circleStyles.sponsorInfo}>
          <p>
            You have added your sponsor&apos;s {saaSponsors.length > 1 ? 'names and emails' : 'name and email'} on your profile.
          </p>
        </div>
      )}
    </div>
  );

  const allCompleted = actions.every((a) => a.completed);

  return (
    <div className={styles.stepPage}>
      <Header breadcrumbs={breadcrumbs} user={user} onLogin={onLogin} onLogout={onLogout} />

      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>Getting Started</h1>
            <StepHelpButton stepKey="app" />
          </div>
          <p className={styles.stepSubtitle}>
            Understand my addiction and define what acting out means for me — before beginning Step One.
          </p>
        </div>

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
              </>
            )}
          </div>
        )}

        {!user && !isInitializing && !errorMessage && <StepLoginPrompt onLogin={onLogin} />}

        {user && isInitializing && !errorMessage && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {user && !isInitializing && !errorMessage && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>Actions to Complete Getting Started</h2>
            <p className={styles.actionsDescription}>
              Complete each action in order. Check the box when you&apos;ve finished each action.
            </p>

            <StepActions
              actions={actions}
              onActionChange={handleActionChange}
              canCompleteAction={canCompleteAction}
              renderActionContent={(action) => {
                if (action.id === 3) {
                  return renderSponsorContent();
                }
                if (action.id === 6) {
                  return renderCirclesPreview();
                }
                return null;
              }}
            />

            {allCompleted && (
              <>
                <div className={styles.completionMessage}>
                  <h3>Congratulations!</h3>
                  <p>You have completed Getting Started and can begin Step One.</p>
                </div>
                <div className={styles.stepNavigation}>
                  <div className={styles.navigationLinks}>
                    <span />
                    <Link to="/saa/12-steps/step-1" className={styles.nextStepLink}>
                      Step One {'>>'}
                    </Link>
                  </div>
                </div>
              </>
            )}

            {!allCompleted && (
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                  <span />
                  <Link to="/saa/12-steps/step-1" className={styles.nextStepLink}>
                    Step One {'>>'}
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <ContentReader
        isOpen={isAddictionReaderOpen}
        onClose={() => setIsAddictionReaderOpen(false)}
        filePath={READINGS_FILE}
        startTag="<<getting-started-addiction start>>"
        endTag="<<getting-started-addiction end>>"
      />
      <ContentReader
        isOpen={isFellowshipReaderOpen}
        onClose={() => setIsFellowshipReaderOpen(false)}
        filePath={READINGS_FILE}
        startTag="<<getting-started-fellowship start>>"
        endTag="<<getting-started-fellowship end>>"
      />
      <ContentReader
        isOpen={is3CirclesReaderOpen}
        onClose={() => setIs3CirclesReaderOpen(false)}
        filePath={READINGS_FILE}
        startTag="<<getting-started-3circles-reading start>>"
        endTag="<<getting-started-3circles-reading end>>"
      />
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="VqWwh_rUZuY"
        title="The 3 Circles of SAA"
      />
      <ThreeCirclesModal
        isOpen={isCirclesModalOpen}
        onClose={() => setIsCirclesModalOpen(false)}
        user={user}
        circles={threeCircles}
        onSave={handleThreeCirclesSave}
      />
      <StepShareDialog
        isOpen={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        user={user}
        stepTitle="Getting Started"
        shareName="Getting Started Summary"
        documentType="getting-started"
        generateHtml={buildGettingStartedShareHtml}
        canGenerate={canShareGettingStarted}
        emptyMessage="Complete at least one action or add to your 3 Circles before sharing."
      />
    </div>
  );
};

export default GettingStarted;
